import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { 
  CalendarIcon, 
  Loader2, 
  CheckCircle2, 
  Star,
  MapPin,
  Clock,
  User,
  Phone,
  Mail,
  ChevronRight,
  ChevronLeft,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service type flow configurations
const SERVICE_TYPE_FLOWS = {
  lesson_class: {
    name: 'Book Class/Lesson',
    steps: ['service', 'session', 'schedule', 'contact', 'confirm'],
    icon: '📚',
  },
  coaching_pt: {
    name: 'Book Personal Training',
    steps: ['service', 'goals', 'details', 'schedule', 'contact', 'confirm'],
    icon: '🏋️',
  },
  repair_maintenance: {
    name: 'Book Repair/Service',
    steps: ['service', 'item', 'issue', 'schedule', 'contact', 'confirm'],
    icon: '🔧',
  },
  setup_installation: {
    name: 'Book Installation',
    steps: ['service', 'item', 'location', 'schedule', 'contact', 'confirm'],
    icon: '🛠️',
  },
  rental: {
    name: 'Book Rental',
    steps: ['items', 'dates', 'delivery', 'contact', 'confirm'],
    icon: '📦',
  },
  guided_experience: {
    name: 'Book Experience',
    steps: ['date', 'group', 'safety', 'contact', 'confirm'],
    icon: '🏕️',
  },
  consultation: {
    name: 'Book Consultation',
    steps: ['type', 'schedule', 'contact', 'confirm'],
    icon: '💬',
  },
};

const SportsBookingModal = ({
  open,
  onClose,
  provider,
  subcategory,
}) => {
  // Determine service type from provider
  const serviceType = provider?.service_types?.[0] || provider?.selectedService?.type || 'lesson_class';
  const flowConfig = SERVICE_TYPE_FLOWS[serviceType] || SERVICE_TYPE_FLOWS.lesson_class;
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookingRef, setBookingRef] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    // Service selection
    selectedService: provider?.selectedService || provider?.services?.[0] || null,
    
    // Session details (lesson_class)
    sessionType: 'group',
    attendeeCount: 1,
    skillLevel: 'beginner',
    
    // Goals (coaching_pt)
    goals: [],
    fitnessLevel: 'beginner',
    healthDisclaimer: false,
    sessionMode: 'in_person',
    
    // Item details (repair/setup)
    itemType: '',
    itemDescription: '',
    issueDescription: '',
    
    // Location (setup/mobile)
    address: '',
    hasStairs: false,
    hasParking: true,
    wallType: 'brick',
    
    // Rental
    rentalItems: [],
    rentalStartDate: null,
    rentalEndDate: null,
    deliveryOption: 'pickup',
    
    // Guided experience
    groupSize: 1,
    ageConfirmed: false,
    safetyDisclaimer: false,
    meetingPoint: '',
    
    // Consultation
    consultationType: 'in_person',
    topic: '',
    duration: '60min',
    
    // Schedule
    preferredDate: null,
    preferredTime: '',
    
    // Contact
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    notes: '',
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const bookingPayload = {
        provider_id: provider.id,
        service_type: serviceType,
        service_name: formData.selectedService?.name || provider.provider_name,
        service_price: formData.selectedService?.price || provider.starting_price,
        customer_name: formData.customerName,
        customer_phone: formData.customerPhone,
        customer_email: formData.customerEmail,
        preferred_date: formData.preferredDate ? format(formData.preferredDate, 'yyyy-MM-dd') : null,
        preferred_time: formData.preferredTime,
        service_address: formData.address,
        notes: formData.notes,
        booking_details: {
          // Include all relevant form data based on service type
          session_type: formData.sessionType,
          attendee_count: formData.attendeeCount,
          skill_level: formData.skillLevel,
          goals: formData.goals,
          fitness_level: formData.fitnessLevel,
          session_mode: formData.sessionMode,
          item_type: formData.itemType,
          item_description: formData.itemDescription,
          issue_description: formData.issueDescription,
          has_stairs: formData.hasStairs,
          has_parking: formData.hasParking,
          wall_type: formData.wallType,
          group_size: formData.groupSize,
          consultation_type: formData.consultationType,
          duration: formData.duration,
          delivery_option: formData.deliveryOption,
        },
      };

      const response = await axios.post(`${API_URL}/api/sports-services/bookings`, bookingPayload);
      
      if (response.data.success) {
        setBookingRef(response.data.booking.booking_ref);
        setSuccess(true);
      }
    } catch (err) {
      console.error('Booking failed:', err);
      alert('Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < flowConfig.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Render step content based on step name
  const renderStepContent = () => {
    const step = flowConfig.steps[currentStep];

    // Service Selection Step with Tier grouping for fitness-gym
    if (step === 'service') {
      // Group services by tier for fitness-gym subcategory
      const services = provider?.services || [];
      const hasTiers = services.some(s => s.tier);
      
      if (hasTiers) {
        const coreServices = services.filter(s => s.tier === 'core');
        const premiumServices = services.filter(s => s.tier === 'premium');
        const conditionalServices = services.filter(s => s.tier === 'conditional');
        const otherServices = services.filter(s => !s.tier);
        
        const renderServiceGroup = (title, icon, groupServices, bgColor = 'bg-gray-50') => {
          if (groupServices.length === 0) return null;
          return (
            <div className="space-y-2">
              <div className={`flex items-center gap-2 px-2 py-1 ${bgColor} rounded`}>
                <span>{icon}</span>
                <span className="text-sm font-medium">{title}</span>
              </div>
              {groupServices.map((service, idx) => (
                <div
                  key={idx}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.selectedService?.name === service.name
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => updateFormData('selectedService', service)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{service.name}</p>
                      <p className="text-sm text-gray-500">{service.description || service.duration}</p>
                    </div>
                    <p className="font-bold">{provider.currency || 'Rs'} {service.price}</p>
                  </div>
                </div>
              ))}
            </div>
          );
        };
        
        return (
          <div className="space-y-4">
            <Label>Select a Service</Label>
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {renderServiceGroup('Core Services (Always Available)', '✅', coreServices, 'bg-green-50')}
              {renderServiceGroup('Premium Services', '⭐', premiumServices, 'bg-amber-50')}
              {renderServiceGroup('Specialized Services', '⚠️', conditionalServices, 'bg-orange-50')}
              {otherServices.length > 0 && renderServiceGroup('Other Services', '📋', otherServices)}
            </div>
          </div>
        );
      }
      
      // Default service list without tiers
      return (
        <div className="space-y-4">
          <Label>Select a Service</Label>
          <div className="space-y-2">
            {provider?.services?.map((service, idx) => (
              <div
                key={idx}
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  formData.selectedService?.name === service.name
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => updateFormData('selectedService', service)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{service.name}</p>
                    <p className="text-sm text-gray-500">{service.description || service.duration}</p>
                  </div>
                  <p className="font-bold">{provider.currency || 'Rs'} {service.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Session Type Step (lesson_class)
    if (step === 'session') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Session Type</Label>
            <Select value={formData.sessionType} onValueChange={(v) => updateFormData('sessionType', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="group">Group Class</SelectItem>
                <SelectItem value="private">Private Session</SelectItem>
                <SelectItem value="online">Online Class</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Number of Attendees</Label>
            <Input
              type="number"
              min="1"
              max="20"
              value={formData.attendeeCount}
              onChange={(e) => updateFormData('attendeeCount', parseInt(e.target.value))}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Skill Level</Label>
            <Select value={formData.skillLevel} onValueChange={(v) => updateFormData('skillLevel', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    // Goals Step (coaching_pt)
    if (step === 'goals') {
      const goalOptions = ['weight_loss', 'strength', 'rehab', 'endurance', 'flexibility'];
      return (
        <div className="space-y-4">
          <div>
            <Label>What are your goals? (Select all that apply)</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {goalOptions.map((goal) => (
                <div
                  key={goal}
                  className={`p-2 border rounded cursor-pointer text-center text-sm ${
                    formData.goals.includes(goal) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => {
                    const newGoals = formData.goals.includes(goal)
                      ? formData.goals.filter(g => g !== goal)
                      : [...formData.goals, goal];
                    updateFormData('goals', newGoals);
                  }}
                >
                  {goal.replace('_', ' ').charAt(0).toUpperCase() + goal.replace('_', ' ').slice(1)}
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Current Fitness Level</Label>
            <Select value={formData.fitnessLevel} onValueChange={(v) => updateFormData('fitnessLevel', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner - New to fitness</SelectItem>
                <SelectItem value="intermediate">Intermediate - Some experience</SelectItem>
                <SelectItem value="advanced">Advanced - Very experienced</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    // Details Step (coaching_pt)
    if (step === 'details') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Session Mode</Label>
            <Select value={formData.sessionMode} onValueChange={(v) => updateFormData('sessionMode', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in_person">In Person</SelectItem>
                <SelectItem value="online">Online (Video Call)</SelectItem>
                <SelectItem value="hybrid">Hybrid (Both)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <Checkbox
              checked={formData.healthDisclaimer}
              onCheckedChange={(v) => updateFormData('healthDisclaimer', v)}
            />
            <Label className="text-sm text-yellow-800">
              I confirm that I'm in good health and have no medical conditions that prevent me from exercising. I understand I should consult a doctor if unsure.
            </Label>
          </div>
        </div>
      );
    }

    // Item Step (repair/setup)
    if (step === 'item') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Item Type</Label>
            <Input
              placeholder="e.g., Road Bike, Treadmill, Basketball Hoop"
              value={formData.itemType}
              onChange={(e) => updateFormData('itemType', e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Item Description</Label>
            <Textarea
              placeholder="Brand, model, size, any relevant details..."
              value={formData.itemDescription}
              onChange={(e) => updateFormData('itemDescription', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
      );
    }

    // Issue Step (repair)
    if (step === 'issue') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Describe the Issue</Label>
            <Textarea
              placeholder="What's wrong? What needs to be fixed or serviced?"
              value={formData.issueDescription}
              onChange={(e) => updateFormData('issueDescription', e.target.value)}
              className="mt-1"
              rows={4}
            />
          </div>

          <p className="text-sm text-gray-500">
            Tip: Include any error messages, sounds, or specific symptoms you've noticed.
          </p>
        </div>
      );
    }

    // Location Step (setup/installation)
    if (step === 'location') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Installation Address *</Label>
            <Textarea
              placeholder="Full address including city and postal code"
              value={formData.address}
              onChange={(e) => updateFormData('address', e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="space-y-3">
            <Label>Access Information</Label>
            
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.hasStairs}
                onCheckedChange={(v) => updateFormData('hasStairs', v)}
              />
              <Label className="text-sm">There are stairs involved</Label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.hasParking}
                onCheckedChange={(v) => updateFormData('hasParking', v)}
              />
              <Label className="text-sm">Parking available nearby</Label>
            </div>

            <div>
              <Label className="text-sm">Wall Type (if mounting required)</Label>
              <Select value={formData.wallType} onValueChange={(v) => updateFormData('wallType', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="brick">Brick</SelectItem>
                  <SelectItem value="drywall">Drywall</SelectItem>
                  <SelectItem value="concrete">Concrete</SelectItem>
                  <SelectItem value="wood">Wood</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );
    }

    // Date/Group Step (guided_experience)
    if (step === 'date' || step === 'group') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Preferred Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left mt-1">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.preferredDate ? format(formData.preferredDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.preferredDate}
                  onSelect={(date) => updateFormData('preferredDate', date)}
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label>Group Size</Label>
            <Input
              type="number"
              min="1"
              max="20"
              value={formData.groupSize}
              onChange={(e) => updateFormData('groupSize', parseInt(e.target.value))}
              className="mt-1"
            />
          </div>
        </div>
      );
    }

    // Safety Step
    if (step === 'safety') {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={formData.ageConfirmed}
              onCheckedChange={(v) => updateFormData('ageConfirmed', v)}
            />
            <Label className="text-sm">I confirm all participants meet the age requirements</Label>
          </div>

          <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
            <Checkbox
              checked={formData.safetyDisclaimer}
              onCheckedChange={(v) => updateFormData('safetyDisclaimer', v)}
            />
            <Label className="text-sm text-orange-800">
              I understand outdoor activities involve inherent risks. I agree to follow all safety instructions provided by the guide.
            </Label>
          </div>

          <div>
            <Label>Meeting Point Notes (Optional)</Label>
            <Input
              placeholder="Any special meeting instructions..."
              value={formData.meetingPoint}
              onChange={(e) => updateFormData('meetingPoint', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
      );
    }

    // Type Step (consultation)
    if (step === 'type') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Consultation Type</Label>
            <Select value={formData.consultationType} onValueChange={(v) => updateFormData('consultationType', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in_person">In Person</SelectItem>
                <SelectItem value="video">Video Call</SelectItem>
                <SelectItem value="phone">Phone Call</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>What would you like to discuss?</Label>
            <Textarea
              placeholder="Topic or questions for the consultation..."
              value={formData.topic}
              onChange={(e) => updateFormData('topic', e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Duration</Label>
            <Select value={formData.duration} onValueChange={(v) => updateFormData('duration', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30min">30 minutes</SelectItem>
                <SelectItem value="60min">60 minutes</SelectItem>
                <SelectItem value="90min">90 minutes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    // Schedule Step
    if (step === 'schedule') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Preferred Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left mt-1">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.preferredDate ? format(formData.preferredDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.preferredDate}
                  onSelect={(date) => updateFormData('preferredDate', date)}
                  disabled={(date) => date < new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label>Preferred Time</Label>
            <Select value={formData.preferredTime} onValueChange={(v) => updateFormData('preferredTime', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select time slot" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="09:00 AM">9:00 AM</SelectItem>
                <SelectItem value="10:00 AM">10:00 AM</SelectItem>
                <SelectItem value="11:00 AM">11:00 AM</SelectItem>
                <SelectItem value="12:00 PM">12:00 PM</SelectItem>
                <SelectItem value="02:00 PM">2:00 PM</SelectItem>
                <SelectItem value="03:00 PM">3:00 PM</SelectItem>
                <SelectItem value="04:00 PM">4:00 PM</SelectItem>
                <SelectItem value="05:00 PM">5:00 PM</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    // Contact Step
    if (step === 'contact') {
      return (
        <div className="space-y-4">
          <div>
            <Label>Your Name *</Label>
            <Input
              placeholder="Full name"
              value={formData.customerName}
              onChange={(e) => updateFormData('customerName', e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Phone Number *</Label>
            <Input
              placeholder="07X XXX XXXX"
              value={formData.customerPhone}
              onChange={(e) => updateFormData('customerPhone', e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Email (Optional)</Label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={formData.customerEmail}
              onChange={(e) => updateFormData('customerEmail', e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Additional Notes</Label>
            <Textarea
              placeholder="Any special requests or information..."
              value={formData.notes}
              onChange={(e) => updateFormData('notes', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
      );
    }

    // Confirm Step
    if (step === 'confirm') {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
            <h4 className="font-medium">Booking Summary</h4>
            
            <div className="flex items-center gap-2 text-sm">
              <Star className="h-4 w-4 text-yellow-500" />
              <span className="font-medium">{provider.provider_name}</span>
            </div>

            <div className="text-sm space-y-1">
              <p><strong>Service:</strong> {formData.selectedService?.name || provider.services?.[0]?.name}</p>
              <p><strong>Date:</strong> {formData.preferredDate ? format(formData.preferredDate, 'PPP') : 'TBD'}</p>
              <p><strong>Time:</strong> {formData.preferredTime || 'TBD'}</p>
              {formData.address && <p><strong>Location:</strong> {formData.address}</p>}
            </div>

            <div className="border-t pt-2 mt-2">
              <p className="text-sm"><strong>Contact:</strong> {formData.customerName}</p>
              <p className="text-sm">{formData.customerPhone}</p>
            </div>

            <div className="border-t pt-2 mt-2 flex justify-between items-center">
              <span className="font-medium">Total</span>
              <span className="font-bold text-lg">
                {provider.currency || 'Rs'} {formData.selectedService?.price || provider.starting_price}
              </span>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            By confirming, you agree to the provider's terms and cancellation policy.
          </p>
        </div>
      );
    }

    return null;
  };

  // Success view
  if (success) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Booking Confirmed!</h2>
            <p className="text-gray-600 mb-4">
              Your booking reference is:
            </p>
            <Badge className="text-lg px-4 py-2 bg-blue-100 text-blue-700">
              {bookingRef}
            </Badge>
            <p className="text-sm text-gray-500 mt-4">
              {provider.provider_name} will contact you shortly to confirm the details.
            </p>
            <Button className="mt-6 w-full" onClick={onClose}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{flowConfig.icon}</span>
            {flowConfig.name}
          </DialogTitle>
          <DialogDescription>
            Book with {provider?.provider_name}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-1 py-2">
          {flowConfig.steps.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 rounded-full transition-all ${
                idx <= currentStep ? 'w-8 bg-blue-500' : 'w-4 bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="min-h-[200px] py-4">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          {currentStep < flowConfig.steps.length - 1 ? (
            <Button onClick={nextStep}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !formData.customerName || !formData.customerPhone}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Booking...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirm Booking
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SportsBookingModal;
